import { AdminActionTypeDto, CandidateItemDto, GenderDto } from '@/api/generated';
import ButtonWithLoading from '@/components/ButtonWithLoading.comp.tsx';
import { useUserContext } from '@/utils/providers/userContext/useUserContext.tsx';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { useInvokeAdminAction } from '@/views/admin/util/useInvokeAdminAction.util.ts';
import React, { useState } from 'react';
import { Button, Dropdown, Form, Modal } from 'react-bootstrap';

interface Props {
  candidate: CandidateItemDto;
  disabled?: boolean;
  onSuccess?: () => void;
}

export const AdminActionsDropdown: React.FC<Props> = ({ candidate, disabled, onSuccess }) => {
  const { t } = useTranslationWithPrefix('views.admin');
  const { user } = useUserContext();
  const { invokeAdminAction, isInvoking } = useInvokeAdminAction();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showGenderModal, setShowGenderModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState<AdminActionTypeDto | null>(null);
  const [selectedGender, setSelectedGender] = useState<GenderDto>(GenderDto.Unknown);

  const handleActionSelect = (action: AdminActionTypeDto) => {
    setSelectedAction(action);

    if (action === AdminActionTypeDto.SetCandidateGender) {
      setShowGenderModal(true);
    } else {
      setShowConfirmModal(true);
    }
  };

  const handleConfirm = async () => {
    if (!user || !selectedAction) return;

    await invokeAdminAction({
      adminUserId: user.userId,
      candidateId: candidate.candidateId,
      dto: {
        actionType: selectedAction,
        gender:
          selectedAction === AdminActionTypeDto.SetCandidateGender ? selectedGender : undefined,
      },
    });

    setShowConfirmModal(false);
    setShowGenderModal(false);
    setSelectedAction(null);

    // Refresh table after successful action
    if (onSuccess) {
      onSuccess();
    }
  };

  const handleCloseModals = () => {
    setShowConfirmModal(false);
    setShowGenderModal(false);
    setSelectedAction(null);
  };

  const getActionMessage = (action: AdminActionTypeDto) => {
    switch (action) {
      case AdminActionTypeDto.SetCandidateNegativeFeedback:
        return t('actions.confirmMessages.negativeFeedback');
      case AdminActionTypeDto.SetCandidatePositiveFeedback:
        return t('actions.confirmMessages.positiveFeedback');
      case AdminActionTypeDto.SetOnHoldAdmin:
        return t('actions.confirmMessages.onHold');
      default:
        return t('actions.confirmMessages.default');
    }
  };

  const getActionLabel = (action: AdminActionTypeDto) => {
    switch (action) {
      case AdminActionTypeDto.SetCandidateGender:
        return t('actions.labels.gender');
      case AdminActionTypeDto.SetCandidateNegativeFeedback:
        return t('actions.labels.negativeFeedback');
      case AdminActionTypeDto.SetCandidatePositiveFeedback:
        return t('actions.labels.positiveFeedback');
      case AdminActionTypeDto.SetOnHoldAdmin:
        return t('actions.labels.onHold');
      default:
        return action;
    }
  };

  const availableActions = candidate.possibleAdminActionTypes || [];
  const hasActions = availableActions.length > 0;

  return (
    <>
      <Dropdown onClick={e => e.stopPropagation()} style={{ width: '100%' }}>
        <Dropdown.Toggle
          disabled={disabled || isInvoking || !hasActions}
          size="sm"
          style={{ minHeight: '30px', padding: '16px 12px', width: '100%' }}
          variant="primary"
        >
          {t('actions.toggle')}
        </Dropdown.Toggle>

        <Dropdown.Menu style={{ minWidth: '250px' }}>
          {availableActions.includes(AdminActionTypeDto.SetCandidatePositiveFeedback) && (
            <Dropdown.Item
              onClick={() => handleActionSelect(AdminActionTypeDto.SetCandidatePositiveFeedback)}
            >
              {getActionLabel(AdminActionTypeDto.SetCandidatePositiveFeedback)}
            </Dropdown.Item>
          )}
          {availableActions.includes(AdminActionTypeDto.SetCandidateNegativeFeedback) && (
            <Dropdown.Item
              onClick={() => handleActionSelect(AdminActionTypeDto.SetCandidateNegativeFeedback)}
            >
              {getActionLabel(AdminActionTypeDto.SetCandidateNegativeFeedback)}
            </Dropdown.Item>
          )}
          {availableActions.includes(AdminActionTypeDto.SetOnHoldAdmin) && (
            <Dropdown.Item onClick={() => handleActionSelect(AdminActionTypeDto.SetOnHoldAdmin)}>
              {getActionLabel(AdminActionTypeDto.SetOnHoldAdmin)}
            </Dropdown.Item>
          )}
          {availableActions.includes(AdminActionTypeDto.SetCandidateGender) && (
            <Dropdown.Item
              onClick={() => handleActionSelect(AdminActionTypeDto.SetCandidateGender)}
            >
              {getActionLabel(AdminActionTypeDto.SetCandidateGender)}
            </Dropdown.Item>
          )}
          {!hasActions && <Dropdown.Item disabled>{t('actions.noActions')}</Dropdown.Item>}
        </Dropdown.Menu>
      </Dropdown>

      {/* Gender selection modal */}
      <Modal
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        onHide={handleCloseModals}
        show={showGenderModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('actions.genderTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>{t('actions.genderLabel')}</Form.Label>
            <Form.Select
              onChange={e => setSelectedGender(e.target.value as GenderDto)}
              value={selectedGender}
            >
              <option value={GenderDto.Male}>{t('table.genders.MALE')}</option>
              <option value={GenderDto.Female}>{t('table.genders.FEMALE')}</option>
              <option value={GenderDto.Unknown}>{t('table.genders.UNKNOWN')}</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={isInvoking} onClick={handleCloseModals} variant="secondary">
            {t('actions.cancel')}
          </Button>
          <ButtonWithLoading
            className="btn btn-primary"
            isLoading={isInvoking}
            onClick={handleConfirm}
          >
            {t('actions.save')}
          </ButtonWithLoading>
        </Modal.Footer>
      </Modal>

      {/* Confirmation modal */}
      <Modal
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        onHide={handleCloseModals}
        show={showConfirmModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('actions.confirmTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{selectedAction && getActionMessage(selectedAction)}</Modal.Body>
        <Modal.Footer>
          <Button disabled={isInvoking} onClick={handleCloseModals} variant="secondary">
            {t('actions.cancel')}
          </Button>
          <ButtonWithLoading
            className="btn btn-primary"
            isLoading={isInvoking}
            onClick={handleConfirm}
          >
            {t('actions.confirm')}
          </ButtonWithLoading>
        </Modal.Footer>
      </Modal>
    </>
  );
};
